/* eslint-disable react/display-name */
import { CSVLink } from 'react-csv';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useRef, useState } from 'react';
import { Table } from '@amzn/awsui-components-react';
import { BigObjectiveRollUp, YTDRollUp } from '@amzn/merp-core/models';

import { TableRow } from 'modules/core/model';
import { ExpandableCell } from 'modules/core/components/Table';
import { AbbreviatedMonths } from 'modules/rollups/constants';
import { RollUpsHeader } from '../RollUpsHeader';
import { CsvConverter } from 'modules/rollups/csv';
import { CSVTemplate } from 'modules/dashboard/models';

type BigObjectiveProps = {
    items: BigObjectiveRollUp[];
    loading?: boolean;
};

export const BigObjective = (props: BigObjectiveProps) => {
    const { items, loading } = props;

    const { t } = useTranslation(['rollups', 'mappings']);

    const [rows, updateRows] = useState<TableRow<BigObjectiveRollUp>[]>([]);
    const [exportLoading, updateExportLoading] = useState(false);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const csvlink = useRef<any>();
    const [csvExport, setCsvExport] = useState<CSVTemplate | undefined>(undefined);

    const onRowExpanded = (rowNumber: number) => {
        const newRows = [...rows];
        const { expanded } = newRows[rowNumber];
        newRows[rowNumber].expanded = !expanded;

        updateRows(newRows);
    };

    const onExport = () => {
        updateExportLoading(true);

        const result = new CsvConverter().bigObjective(rows.map(r => r.payload));
        setCsvExport(result);
    };

    useEffect(() => {
        if (!csvExport) return;

        // The csv library needs some time to load the csv data and make the blob.
        // this effect runs before the ref is rendered, so we ned to wait for the
        // ref to be rendered before triggering the click
        setTimeout(() => {
            csvlink.current?.link.click();
            updateExportLoading(false);
            setCsvExport(undefined);
        }, 500);
    }, [csvExport]);

    useEffect(() => {
        updateRows(prev => {
            if (prev.length === 0) {
                return items.map((payload, number) => ({
                    number,
                    payload,
                    expanded: false,
                    selected: false,
                    rowId: payload.bigObjective
                }));
            } else {
                return items.map((payload, i) => Object.assign(prev[i], { payload, rowId: payload.bigObjective }));
            }
        });
    }, [items]);

    function getCountryCountString(e: TableRow<BigObjectiveRollUp>) {
        if (Object.keys(e.payload.countries).length === 0) {
            return '';
        } else if (Object.keys(e.payload.countries).length === 1) {
            return ' (1 country)';
        } else {
            return `(${Object.keys(e.payload.countries).length} countries)`;
        }
    }

    const columns = [
        {
            id: 'bigObjective',
            header: <div>{t('rollups:bigObjective')}</div>,
            cell: (e: TableRow<BigObjectiveRollUp>) => (
                <ExpandableCell
                    row={e}
                    cell={
                        <span className="mt-auto mb-auto">
                            {`${t('mappings:dropdown.bigOList.' + e.payload.bigObjective)} ${getCountryCountString(e)}`}
                        </span>
                    }
                    control={Object.keys(e.payload.countries).length > 0}
                    content={Object.keys(e.payload.countries).map((c: string) => (
                        <span key={c}>{c}</span>
                    ))}
                    onChange={onRowExpanded}
                />
            )
        },
        {
            id: 'YTM',
            header: <div>{t('rollups:YTM')}</div>,
            cell: (e: TableRow<BigObjectiveRollUp>) => (
                <ExpandableCell
                    row={e}
                    cell={<>{e.payload.ytd}</>}
                    content={Object.values(e.payload.countries).map((c: YTDRollUp, i) => (
                        <span key={`${c.year}-${i}`}></span>
                    ))}
                />
            )
        },
        ...AbbreviatedMonths.map(month => {
            const header = month.charAt(0).toUpperCase() + month.slice(-month.length + 1);

            return {
                id: month,
                header,
                cell: (e: TableRow<BigObjectiveRollUp>) => (
                    <ExpandableCell
                        row={e}
                        cell={<>{e.payload[month]}</>}
                        content={Object.entries(e.payload.countries).map(([key, c]: [string, YTDRollUp]) => (
                            <span key={`${month}-${key}`}>{c[month]}</span>
                        ))}
                    />
                )
            };
        })
    ];

    let header = t('rollups:bigObjective');

    if (rows.length > 0) {
        header += ` (${rows.length})`;
    }

    return (
        <div className="msp-expandable-table">
            {csvExport && (
                <CSVLink
                    ref={csvlink}
                    data={csvExport.getCSVData()}
                    headers={csvExport.getHeaders()}
                    filename={'msp_big_o_rollups.csv'}
                    className="hidden"
                    target="_blank"
                />
            )}
            <Table
                columnDefinitions={columns}
                items={rows}
                loading={loading}
                loadingText={t('rollups:loading')}
                header={
                    <RollUpsHeader
                        disabled={rows.length <= 0}
                        header={header}
                        onExport={onExport}
                        exportLoading={exportLoading}
                    />
                }
            />
        </div>
    );
};
